/**
 * Classical Yogas Loader
 * 
 * Dynamically loads yoga definitions from classical_yogas table
 * and converts them into Rule objects for the feeds V2 engine.
 * 
 * This bridges the database definitions with the existing trigger/score system.
 */

import { supabase } from '../../utils/supabaseClient';
import type { DashaContext, TransitContext, LifeArea, HouseNum, PlanetName } from './types';
import { getHouseLord, getHousesRuledByPlanet, getHouseSign } from '../../utils/lordshipUtils';

// Import from classical_yogas.json structure
export interface ClassicalYogaDefinition {
  id: string;
  name: string;
  category: string;
  source: {
    text: string;
    chapter: number;
    section: string;
    author: string;
    page: number;
  };
  condition: {
    reference_point: string;
    relative_positions: Array<{
      pattern_type: string;
      [key: string]: any;
    }>;
    additional_requirements?: any[];
  };
  prediction: {
    summary: string;
    traits: string[];
    life_areas: string[];
    timing_notes: string;
  };
  remedy: any;
  planets_involved: string[];
  reference_planet: string | null;
  yoga_type: 'benefic' | 'malefic' | 'mixed' | 'neutral';
  strength_range: 'weak' | 'moderate' | 'strong' | 'very_strong';
  priority: number;
  is_rare: boolean;
}

/**
 * Fetch all classical yogas from Supabase
 */
export async function fetchClassicalYogas(): Promise<ClassicalYogaDefinition[]> {
  const { data, error } = await supabase
    .from('classical_yogas')
    .select('*')
    .order('priority', { ascending: false });

  if (error) {
    console.error('[fetchClassicalYogas] Error:', error);
    return [];
  }

  return (data || []) as ClassicalYogaDefinition[];
}

/**
 * Convert classical yoga definition to a Rule-compatible trigger function
 */
export function createTriggerFunction(yoga: ClassicalYogaDefinition) {
  return (dasha: DashaContext, transits: TransitContext): boolean => {
    if (!dasha.natalPlacements) {
      console.warn(`[${yoga.id}] No natal placements available`);
      return false;
    }

    const { condition } = yoga;
    const { reference_point, relative_positions, additional_requirements = [] } = condition;

    try {
      // Evaluate each relative position pattern
      for (const pattern of relative_positions) {
        const result = evaluatePattern(pattern, dasha, reference_point);
        if (!result) return false;
      }

      // Evaluate additional requirements
      for (const req of additional_requirements) {
        const result = evaluateAdditionalRequirement(req, dasha);
        if (!result) return false;
      }

      return true;
    } catch (err) {
      console.error(`[${yoga.id}] Trigger evaluation error:`, err);
      return false;
    }
  };
}

/**
 * Pattern matcher - evaluates condition.relative_positions patterns
 */
function evaluatePattern(
  pattern: any,
  dasha: DashaContext,
  referencePoint: string
): boolean {
  const { pattern_type } = pattern;

  switch (pattern_type) {
    case 'house_from_reference':
      return checkHouseFromReference(pattern, dasha, referencePoint);
    
    case 'planet_in_house':
      return checkPlanetInHouse(pattern, dasha);
    
    case 'planets_in_kendra':
      return checkPlanetsInKendra(pattern, dasha);
    
    case 'planets_in_trikona':
      return checkPlanetsInTrikona(pattern, dasha);
    
    case 'benefics_relative_to_reference':
      return checkBeneficsRelativeToReference(pattern, dasha, referencePoint);
    
    case 'parivartana':
      return checkParivartana(pattern, dasha);
    
    case 'lord_placement':
      return checkLordPlacement(pattern, dasha);
    
    case 'ninth_lord_in_kendra':
      return checkNinthLordInKendra(pattern, dasha);
    
    case 'benefics_in_kendra_trikona_or_2':
      return checkBeneficsInKendraTrikona(pattern, dasha);
    
    case 'benefics_in_upachayas':
      return checkBeneficsInUpachayas(pattern, dasha);
    
    case 'moon_relative_to_sun':
      return checkMoonRelativeToSun(pattern, dasha);
    
    case 'conjunction':
      return checkConjunction(pattern, dasha);
    
    case 'fixed_lagna':
      return checkFixedLagna(pattern, dasha);
    
    case 'sixth_lord_in_own_house':
    case 'eighth_lord_in_own_house':
    case 'twelfth_lord_in_own_house':
      return checkLordInOwnHouse(pattern, dasha);
    
    default:
      // Silently return false for unimplemented pattern types
      // console.warn(`[evaluatePattern] Unknown pattern_type: ${pattern_type}`);
      return false;
  }
}

/**
 * Pattern: house_from_reference
 * Example: "planets in 2nd from Sun"
 */
function checkHouseFromReference(
  pattern: any,
  dasha: DashaContext,
  referencePoint: string
): boolean {
  const { house_from_reference, allowed_planets, min_planets = 1 } = pattern;
  
  // Get reference planet's house
  const refPlanet = referencePoint as PlanetName;
  const refPlacement = dasha.natalPlacements?.[refPlanet];
  if (!refPlacement) return false;
  
  const refHouse = refPlacement.house;
  const targetHouse = ((refHouse - 1 + house_from_reference - 1) % 12) + 1 as HouseNum;
  
  // Find planets in target house
  const planetsInTarget: PlanetName[] = [];
  for (const [planet, placement] of Object.entries(dasha.natalPlacements || {})) {
    if (placement.house === targetHouse) {
      if (allowed_planets === 'all_except_moon' && planet !== 'Moon') {
        planetsInTarget.push(planet as PlanetName);
      } else if (allowed_planets !== 'all_except_moon') {
        planetsInTarget.push(planet as PlanetName);
      }
    }
  }
  
  return planetsInTarget.length >= min_planets;
}

/**
 * Pattern: planet_in_house
 * Example: "Jupiter in 5th house from lagna"
 */
function checkPlanetInHouse(pattern: any, dasha: DashaContext): boolean {
  const { planet, house_from_lagna } = pattern;
  const placement = dasha.natalPlacements?.[planet as PlanetName];
  return placement?.house === house_from_lagna;
}

/**
 * Pattern: planets_in_kendra
 * Kendra = 1, 4, 7, 10
 */
function checkPlanetsInKendra(pattern: any, dasha: DashaContext): boolean {
  const { planet } = pattern;
  const kendras: HouseNum[] = [1, 4, 7, 10];
  const placement = dasha.natalPlacements?.[planet as PlanetName];
  return placement ? kendras.includes(placement.house) : false;
}

/**
 * Pattern: planets_in_trikona
 * Trikona = 1, 5, 9
 */
function checkPlanetsInTrikona(pattern: any, dasha: DashaContext): boolean {
  const { planet } = pattern;
  const trikonas: HouseNum[] = [1, 5, 9];
  const placement = dasha.natalPlacements?.[planet as PlanetName];
  return placement ? trikonas.includes(placement.house) : false;
}

/**
 * Pattern: benefics_relative_to_reference
 * Example: "Jupiter, Venus, Mercury in 4th, 10th, 11th from lagna lord"
 */
function checkBeneficsRelativeToReference(
  pattern: any,
  dasha: DashaContext,
  referencePoint: string
): boolean {
  const { houses_from_reference } = pattern;
  const benefics: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
  
  // Get reference planet/point
  let refHouse: HouseNum;
  if (referencePoint === 'LagnaLord' || referencePoint === 'Lagna') {
    refHouse = 1; // Simplified - would need actual lagna lord lookup
  } else {
    const refPlacement = dasha.natalPlacements?.[referencePoint as PlanetName];
    if (!refPlacement) return false;
    refHouse = refPlacement.house;
  }
  
  // Check if benefics are in specified houses from reference
  const targetHouses = houses_from_reference.map((offset: number) => 
    ((refHouse - 1 + offset - 1) % 12) + 1 as HouseNum
  );
  
  return benefics.every(planet => {
    const placement = dasha.natalPlacements?.[planet];
    return placement && targetHouses.includes(placement.house);
  });
}

/**
 * Pattern: parivartana (mutual exchange)
 * Example: "2nd lord and 10th lord exchange signs"
 */
function checkParivartana(pattern: any, dasha: DashaContext): boolean {
  const { planets } = pattern;
  if (planets.length !== 2) return false;
  
  const [planet1Desc, planet2Desc] = planets;
  
  // This is simplified - needs actual lord calculation
  // For now, check if two specific planets are in each other's signs
  // console.warn('[checkParivartana] Parivartana detection not fully implemented yet');
  return false;
}

/**
 * Pattern: lord_placement
 * Example: "10th lord in 5th house"
 */
function checkLordPlacement(pattern: any, dasha: DashaContext): boolean {
  const { planet, houses_from_lagna } = pattern;
  
  // Simplified - needs actual lord calculation
  // console.warn('[checkLordPlacement] Lord placement detection not fully implemented yet');
  return false;
}

/**
 * Pattern: ninth_lord_in_kendra
 */
function checkNinthLordInKendra(pattern: any, dasha: DashaContext): boolean {
  const { houses_allowed, sign_condition } = pattern;
  
  // Needs lord calculation system
  // console.warn('[checkNinthLordInKendra] Not fully implemented yet');
  return false;
}

/**
 * Pattern: benefics_in_kendra_trikona_or_2
 */
function checkBeneficsInKendraTrikona(pattern: any, dasha: DashaContext): boolean {
  const { planets, allowed_houses } = pattern;
  
  return planets.every((planet: string) => {
    const placement = dasha.natalPlacements?.[planet as PlanetName];
    return placement && allowed_houses.includes(placement.house);
  });
}

/**
 * Pattern: benefics_in_upachayas
 * Upachayas = 3, 6, 10, 11
 */
function checkBeneficsInUpachayas(pattern: any, dasha: DashaContext): boolean {
  const { planets, upachayas } = pattern;
  
  return planets.some((planet: string) => {
    const placement = dasha.natalPlacements?.[planet as PlanetName];
    return placement && upachayas.includes(placement.house);
  });
}

/**
 * Pattern: moon_relative_to_sun
 */
function checkMoonRelativeToSun(pattern: any, dasha: DashaContext): boolean {
  const { houses_from_sun } = pattern;
  const sunPlacement = dasha.natalPlacements?.Sun;
  const moonPlacement = dasha.natalPlacements?.Moon;
  
  if (!sunPlacement || !moonPlacement) return false;
  
  const sunHouse = sunPlacement.house;
  const moonHouse = moonPlacement.house;
  const offset = ((moonHouse - sunHouse + 12) % 12) || 12;
  
  return offset === houses_from_sun;
}

/**
 * Pattern: conjunction
 */
function checkConjunction(pattern: any, dasha: DashaContext): boolean {
  const { planets } = pattern;
  
  // Defensive check: ensure planets array exists
  if (!planets || !Array.isArray(planets) || planets.length < 2) return false;
  
  const placements = planets.map((p: string) => 
    dasha.natalPlacements?.[p as PlanetName]
  );
  
  if (placements.some((p: any) => !p)) return false;
  
  // All planets must be in the same house
  const firstHouse = placements[0]?.house;
  if (!firstHouse) return false;
  return placements.every((p: any) => p?.house === firstHouse);
}

/**
 * Pattern: fixed_lagna
 * Fixed signs = Taurus, Leo, Scorpio, Aquarius (1, 4, 7, 10 in 0-indexed)
 */
function checkFixedLagna(pattern: any, dasha: DashaContext): boolean {
  // Need ascendant sign from context
  // For now, return false
  // console.warn('[checkFixedLagna] Needs ascendant sign from context');
  return false;
}

/**
 * Pattern: lord in own house (Vipareeta Raja Yogas)
 */
function checkLordInOwnHouse(pattern: any, dasha: DashaContext): boolean {
  const { house } = pattern;
  
  // Simplified - needs lord calculation
  // console.warn('[checkLordInOwnHouse] Not fully implemented yet');
  return false;
}

/**
 * Evaluate additional requirements
 */
function evaluateAdditionalRequirement(req: any, dasha: DashaContext): boolean {
  // Handle various requirement types
  // For now, return true to not block yogas
  return true;
}

/**
 * Create score function for a yoga
 */
export function createScoreFunction(yoga: ClassicalYogaDefinition) {
  return (dasha: DashaContext, transits: TransitContext): { value: number; notes: string[] } => {
    const notes: string[] = [];
    
    notes.push(`✨ ${yoga.name.toUpperCase()}`);
    notes.push(`📖 ${yoga.source.text} - Ch ${yoga.source.chapter}, p${yoga.source.page}`);
    notes.push(`🎯 ${yoga.prediction.summary}`);
    
    // Add traits
    if (yoga.prediction.traits.length > 0) {
      notes.push(`TRAITS: ${yoga.prediction.traits.join(', ')}`);
    }
    
    // Add timing notes
    notes.push(`⏰ ${yoga.prediction.timing_notes}`);
    
    // Add planets involved
    if (yoga.planets_involved.length > 0) {
      notes.push(`PLANETS: ${yoga.planets_involved.join(', ')}`);
    }
    
    // Calculate score based on strength and type
    let baseScore = 50;
    
    // Strength multiplier
    switch (yoga.strength_range) {
      case 'very_strong': baseScore = 70; break;
      case 'strong': baseScore = 60; break;
      case 'moderate': baseScore = 50; break;
      case 'weak': baseScore = 40; break;
    }
    
    // Type polarity
    const score = yoga.yoga_type === 'malefic' ? -baseScore : baseScore;
    
    return { value: score, notes };
  };
}

/**
 * Valid life areas from types.ts
 */
const VALID_LIFE_AREAS = new Set([
  'Self', 'Mother', 'Father', 'Spouse', 'Children',
  'Wealth', 'Career', 'Property', 'Health', 'Travel',
  'Investments', 'Litigation', 'Spirituality', 'AccidentRisk'
]);

/**
 * Filter and validate life areas, mapping invalid ones to valid alternatives
 */
function normalizeLifeAreas(rawAreas: string[]): LifeArea[] {
  const areas: LifeArea[] = [];
  
  for (const area of rawAreas) {
    // Skip meta-areas and invalid values
    if (area.startsWith('any_area') || 
        area.startsWith('houses_occupied') || 
        area.startsWith('karaka_significations')) {
      continue;
    }
    
    // Only add valid life areas
    if (VALID_LIFE_AREAS.has(area)) {
      areas.push(area as LifeArea);
    }
  }
  
  // If no valid areas remain, default to 'Self' as a fallback
  return areas.length > 0 ? areas : ['Self'];
}

/**
 * Convert classical yogas to Rules format
 */
export function convertYogasToRules(yogas: ClassicalYogaDefinition[]): any[] {
  // Filter out remedy guidelines and general rules (not actual yogas)
  const actualYogas = yogas.filter(yoga => 
    yoga.category !== 'Remedy Guideline' && 
    !yoga.id.startsWith('remedy_')
  );
  
  return actualYogas.map(yoga => ({
    key: yoga.id,
    priority: yoga.priority,
    areas: normalizeLifeAreas(yoga.prediction.life_areas),
    templateKey: 'generic', // Use generic template as fallback
    customPrediction: `${yoga.name}: ${yoga.prediction.summary}`, // Use yoga name + summary as message
    ruleType: 'natal' as const,
    triggers: createTriggerFunction(yoga),
    score: createScoreFunction(yoga)
  }));
}
